import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { MajorStoryDetail } from '@/components/MajorStoryDetail';
import { majorStoriesService, type MajorStoriesPayload } from '@/services/api';

export function MajorStoriesPage() {
  const navigate = useNavigate();
  const { rank: rankParam } = useParams<{ rank?: string }>();
  const [payload, setPayload] = useState<MajorStoriesPayload | null>(null);
  const [loading, setLoading] = useState(true);

  const selectedRank = rankParam ? Number.parseInt(rankParam, 10) : null;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await majorStoriesService.getLatest();
        if (!cancelled) {
          setPayload(response);
        }
      } catch (error) {
        console.error('Failed to load major stories:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const stories = payload?.stories ?? [];
  const selectedStory = useMemo(() => {
    if (selectedRank == null || Number.isNaN(selectedRank)) return null;
    return stories.find((story) => story.rank === selectedRank) ?? null;
  }, [stories, selectedRank]);

  const isSingleStoryView = selectedRank != null && !Number.isNaN(selectedRank);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-gray-900">
              {isSingleStoryView && selectedStory ? selectedStory.title : 'Major Stories'}
            </h1>
            {payload?.metadata?.week_label ? (
              <p className="text-sm text-gray-500">{payload.metadata.week_label}</p>
            ) : null}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        {loading ? (
          <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white p-12">
            <Loader2 className="mr-3 h-6 w-6 animate-spin text-gray-400" />
            <span className="text-gray-500">Loading major stories...</span>
          </div>
        ) : !payload || stories.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center">
            <p className="text-gray-500">No major stories available yet.</p>
          </div>
        ) : isSingleStoryView && !selectedStory ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center">
            <p className="text-gray-500">Story not found.</p>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="mt-4 text-sm text-gray-600 underline hover:text-gray-900"
            >
              Back to dashboard
            </button>
          </div>
        ) : isSingleStoryView && selectedStory ? (
          <MajorStoryDetail
            story={selectedStory}
            weekLabel={payload.metadata?.week_label}
          />
        ) : null}
      </main>
    </div>
  );
}
